/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useCallback, useState } from "react";
import { useAuth } from "./useAuth";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeContent, validateEmail } from "@/lib/sanitizer";
import { logger } from "@/lib/logger";

interface SecurityAlert {
  id: string;
  type: "high" | "medium" | "low";
  message: string;
  timestamp: number;
  requiresAction: boolean;
}

export const useEnhancedSecurity = () => {
  const { user, profile } = useAuth();
  const [securityAlerts, setSecurityAlerts] = useState<SecurityAlert[]>([]);

  // Helper function to get operation limits
  const getOperationLimit = (operation: string) => {
    const limits = {
      profile_update: { max: 3, window: 300000 },
      journey_create: { max: 2, window: 600000 },
      login_attempt: { max: 5, window: 900000 },
      role_check: { max: 10, window: 60000 },
      resource_access: { max: 50, window: 60000 },
    };
    return (
      limits[operation as keyof typeof limits] || { max: 10, window: 60000 }
    );
  };

  // Enhanced server-side rate limiting check
  const checkServerRateLimit = useCallback(
    async (operation: string): Promise<boolean> => {
      if (!user?.id) return false;

      try {
        const { data, error } = await supabase.rpc(
          "enhanced_rate_limit_check",
          {
            p_user_id: user.id,
            p_operation: operation,
            p_ip_address: null, // IP is captured server-side
            p_max_requests: getOperationLimit(operation).max,
            p_window_minutes: Math.floor(
              getOperationLimit(operation).window / 60000
            ),
          }
        );

        if (error) {
          logger.error("Rate limit check failed:", error);
          return false;
        }

        return data;
      } catch (error) {
        logger.error("Rate limit check error:", error);
        return false;
      }
    },
    [user]
  );

  // Enhanced authorization check
  const checkServerAuthorization = useCallback(
    async (
      operation: string,
      resourceUserId?: string,
      additionalContext?: Record<string, any>
    ): Promise<boolean> => {
      if (!user) return false;

      try {
        const { data, error } = await supabase.rpc(
          "enhanced_authorize_operation",
          {
            p_operation: operation,
            p_resource_user_id: resourceUserId,
            p_additional_context: additionalContext
              ? JSON.stringify(additionalContext)
              : null,
          }
        );

        if (error) {
          logger.error("Authorization check failed:", error);
          return false;
        }

        return data;
      } catch (error) {
        logger.error("Authorization check error:", error);
        return false;
      }
    },
    [user]
  );

  // Monitor session activity
  const monitorSessionActivity = useCallback(
    async (activityType: string, sessionData?: Record<string, any>) => {
      if (!user?.id) return;

      try {
        await supabase.rpc("monitor_session_activity", {
          p_user_id: user.id,
          p_activity_type: activityType,
          p_session_data: sessionData ? JSON.stringify(sessionData) : null,
        });
      } catch (error) {
        logger.error("Session monitoring failed:", error);
      }
    },
    [user]
  );

  // Enhanced role checking with server-side validation
  const requireRole = useCallback(
    async (requiredRole: "client" | "therapist"): Promise<boolean> => {
      if (!user || !profile) return false;

      const authorized = await checkServerAuthorization("role_check", user.id, {
        required_role: requiredRole,
        current_role: profile.role,
      });

      if (!authorized || profile.role !== requiredRole) {
        await monitorSessionActivity("unauthorized_role_access", {
          required_role: requiredRole,
          current_role: profile.role,
        });
        return false;
      }

      return true;
    },
    [user, profile, checkServerAuthorization, monitorSessionActivity]
  );

  // Enhanced resource access checking
  const canAccessResource = useCallback(
    async (resourceUserId?: string, operation?: string): Promise<boolean> => {
      if (!user || !profile) return false;

      const authorized = await checkServerAuthorization(
        "resource_access",
        resourceUserId,
        {
          operation,
          user_role: profile.role,
        }
      );

      if (!authorized) {
        await monitorSessionActivity("unauthorized_resource_access", {
          operation,
          resource_user_id: resourceUserId,
        });
      }

      return authorized;
    },
    [user, profile, checkServerAuthorization, monitorSessionActivity]
  );

  // Secure profile update with comprehensive validation (respecting new RLS constraints)
  const secureProfileUpdate = useCallback(
    async (updates: { full_name?: string; current_week?: number }) => {
      if (!user || !profile) {
        throw new Error("Authentication required");
      }

      // Check server-side rate limiting
      const rateLimitOk = await checkServerRateLimit("profile_update");
      if (!rateLimitOk) {
        throw new Error(
          "Rate limit exceeded. Please wait before updating your profile again."
        );
      }

      // Server-side authorization check
      const authorized = await checkServerAuthorization(
        "profile_update",
        user.id
      );
      if (!authorized) {
        throw new Error("Unauthorized operation");
      }

      // Enhanced input validation - only safe fields allowed by new RLS policy
      const validatedUpdates: { full_name?: string } = {}; // Remove current_week from client updates

      if (updates.full_name !== undefined) {
        const sanitized = sanitizeContent(updates.full_name);
        if (sanitized.length === 0 || sanitized.length > 100) {
          throw new Error("Full name must be between 1 and 100 characters");
        }
        validatedUpdates.full_name = sanitized;
      }

      // Note: current_week updates are now restricted by RLS policy
      // Only therapists can modify current_week for assigned clients through secure functions
      if (updates.current_week !== undefined && profile.role === "therapist") {
        throw new Error(
          "Week updates must be done through therapist-specific functions"
        );
      }

      // Perform the update (will be restricted by new RLS policies)
      const { error } = await supabase
        .from("profiles")
        .update(validatedUpdates)
        .eq("id", user.id);

      if (error) {
        await monitorSessionActivity("profile_update_failed", {
          error: error.message,
          attempted_updates: Object.keys(validatedUpdates),
        });
        throw error;
      }

      await monitorSessionActivity("profile_update_success", {
        updated_fields: Object.keys(validatedUpdates),
      });
    },
    [
      user,
      profile,
      checkServerRateLimit,
      checkServerAuthorization,
      monitorSessionActivity,
    ]
  );

  // REMOVED: Duplicate real-time security alerts monitoring
  // This functionality is now handled by useSecurityAlerts hook to avoid duplicate subscriptions
  // Keeping this comment for reference - the subscription was causing 2x realtime overhead

  // Session monitoring on auth state changes
  // OPTIMIZATION: Only enable in production to reduce CPU usage (disabled for dev performance)
  useEffect(() => {
    // Only enable if explicitly configured
    const enableSessionMonitoring =
      import.meta.env.VITE_ENABLE_SESSION_MONITORING === "true";

    if (!enableSessionMonitoring) {
      logger.debug(
        "[Performance] Session monitoring disabled for dev environment"
      );
      return;
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        // Defer to avoid blocking auth state change
        setTimeout(() => {
          monitorSessionActivity("login", {
            user_agent: navigator.userAgent,
            timestamp: Date.now(),
          });
        }, 0);
      } else if (event === "SIGNED_OUT") {
        setTimeout(() => {
          monitorSessionActivity("logout", {
            timestamp: Date.now(),
          });
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [monitorSessionActivity]);

  // Clear security alert
  const clearSecurityAlert = useCallback((alertId: string) => {
    setSecurityAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  }, []);

  return {
    requireRole,
    canAccessResource,
    checkServerRateLimit,
    checkServerAuthorization,
    secureProfileUpdate,
    monitorSessionActivity,
    sanitizeContent,
    validateEmail,
    getOperationLimit,
    securityAlerts,
    clearSecurityAlert,
  };
};
